import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Eye, Download, FileText } from "lucide-react"
import { PDFPreview } from "./PDFPreview"
import { api, type ExamPaper } from "@/lib/api"

interface ExamPaperPreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  paper: ExamPaper | null
}

export function ExamPaperPreviewDialog({ open, onOpenChange, paper }: ExamPaperPreviewDialogProps) {
  const [showPreview, setShowPreview] = useState(false)


  const handlePreviewClick = () => {
    if (paper?.id) {
      // Open PDF in new tab for viewing
      const link = document.createElement('a')
      link.href = api.getPreviewUrl(paper.id)
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  // Reset preview state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setShowPreview(false)
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`w-[90vw] max-w-4xl sm:w-full ${showPreview ? 'h-[90vh] flex flex-col' : 'max-h-[90vh] overflow-y-auto'}`}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{paper?.title}</DialogTitle>
          <DialogDescription>
            {paper?.year}年 · {paper?.subject} · {paper?.region}
          </DialogDescription>
        </DialogHeader>
        <div className={`${showPreview ? 'flex flex-col flex-1 min-h-0 gap-4' : 'space-y-4'}`}>
          {showPreview && paper?.id ? (
            <div className="flex-1 min-h-0">
              <PDFPreview url={api.getPreviewUrl(paper.id)} />
            </div>
          ) : (
            <>
              <div>
                <h4 className="mb-2 text-sm font-semibold">试卷信息</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>建议用时：{paper?.duration} 分钟</p>
                  {paper?.difficulty && <p>难度：{paper.difficulty}</p>}
                  {paper?.tags && paper.tags.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span>标签：</span>
                      <div className="flex gap-1">
                        {paper.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="bg-primary/10 text-xs text-primary">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {paper?.description && (
                <div>
                  <h4 className="mb-2 text-sm font-semibold">试卷简介</h4>
                  <p className="text-sm text-muted-foreground">{paper.description}</p>
                </div>
              )}
              {paper?.suggestion && (
                <div>
                  <h4 className="mb-2 text-sm font-semibold">做题建议</h4>
                  <p className="text-sm text-muted-foreground">{paper.suggestion}</p>
                </div>
              )}
            </>
          )}
          <div className="flex items-center justify-between border-t pt-4 flex-shrink-0">
            <div className="flex gap-2 md:gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                <span className="hidden sm:inline">{paper?.views || 0} 次查看</span>
                <span className="sm:hidden">{paper?.views || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">{paper?.downloads || 0} 次下载</span>
                <span className="sm:hidden">{paper?.downloads || 0}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handlePreviewClick}
              >
                <FileText className="mr-0.5 md:mr-1 h-4 w-4" />
                预览
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a
                  href={paper?.id ? api.getDownloadUrl(paper.id) : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="mr-0.5 md:mr-1 h-4 w-4" />
                  下载
                </a>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
